/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Select, { StylesConfig } from 'react-select';
import { generateUrl } from '../../routes/util';
import { getInitiativeTree } from '../../selectors/initiativeTree';
import { Tree, ReportingLevelInterface, ReportingLevelOption } from '../../types/tree';
import { RouteInterface } from '../../types/routes';
import { unloadInitiativeSurveyList } from '../../slice/initiativeSurveyListSlice';
import { useIsPortfolioTrackerExchangeView } from '../../hooks/useIsPortfolioTrackerExchangeView';

export const createOptions = (reportingLevelTree: Tree) => {

  const hasReportingLevels = reportingLevelTree.children.length > 0;
  const maxNestedOptions = 6;
  const reportingLevelOptions: ReportingLevelOption[] = [];

  if (!hasReportingLevels) {
    return reportingLevelOptions;
  }

  const populateReportingLevelOptions = (level: ReportingLevelInterface, nested: number, parentId: string) => {
    reportingLevelOptions.push({
      value: level.id,
      label: level.name,
      parentId: parentId,
      isHidden: nested > maxNestedOptions,
      nested: nested,
    });
    if (level.children && level.children.length > 0) {
      level.children.forEach(child => populateReportingLevelOptions(child, nested + 1, level.id));
    }
  }
  reportingLevelTree.children.forEach(child => populateReportingLevelOptions(child, 1, reportingLevelTree.id));

  return reportingLevelOptions;
};

interface ReportingLevelDropdownParams {
  route: RouteInterface;
  initiativeTree?: Tree;
  placeholder?: string;
  styles?: StylesConfig;
}

export const ReportingLevelDropdown = (props: ReportingLevelDropdownParams) => {
  const { route, initiativeTree, styles, placeholder = 'Select your Reporting Level' } = props;
  const { initiativeId = '', portfolioId } = useParams<{ initiativeId?: string, portfolioId?: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const baseTree = useSelector(getInitiativeTree);
  const isPortfolioTrackerExchangeView = useIsPortfolioTrackerExchangeView();

  const reportingLevelTree = initiativeTree ?? baseTree;
  const reportingLevelOptions = createOptions(reportingLevelTree);

  if (reportingLevelOptions.length <= 1) {
    return null;
  }

  const selectedUrlId = portfolioId ?? initiativeId;

  const onReportingLevelChange = (value: any) => {
    // Dropdown value
    const [id] = value.value.split('_');
    dispatch(unloadInitiativeSurveyList()); // This is to force reset of the select report on the summary page
    history.push(generateUrl(route, { initiativeId: id, portfolioId: id }));
  };

  const selectedReportingLevel = reportingLevelOptions.find(o => selectedUrlId && o.value === selectedUrlId);

  const unhideSelectedOptions = (initiativeId: string) => {
    const option = reportingLevelOptions.find(option => option.value === initiativeId);
    if (option) {
      // Unhide self
      option.isHidden = false;
      // Unhide all parents
      if (option.parentId) {
        unhideSelectedOptions(option.parentId);
      }
      // Unhide direct children
      reportingLevelOptions.filter(option => option.parentId === initiativeId).forEach(option => option.isHidden = false);
    }
  }

  if (selectedReportingLevel && initiativeId) {
    unhideSelectedOptions(initiativeId);
  }

  const nestedPadding = 12;
  const customStyles: StylesConfig = styles ?? {
    option: (provided, state: any) => {
      return {
        ...provided,
        display: state.data.isHidden ? 'none' : 'inline-block',
        paddingLeft: `${state.data.nested * nestedPadding}px`,
        fontWeight: state.data.nested === 1 ? 'bold' : 'normal',
      };
    },
    ...(isPortfolioTrackerExchangeView && {
      control: (provided) => {
        return {
          ...provided,
          borderColor: 'var(--theme-BorderDefault)',
        };
      },
    }),
  };

  return (
    <Select
      placeholder={placeholder}
      className='w-100'
      isMulti={false}
      styles={customStyles}
      onChange={onReportingLevelChange}
      value={selectedReportingLevel ?? null}
      isSearchable={true}
      options={reportingLevelOptions}
    />
  );
}
